import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FaEdit, FaCamera, FaUserPlus, FaUserMinus,
  FaMapMarkerAlt, FaLink, FaCalendarAlt, FaThumbtack,
  FaCog, FaCheckCircle, FaBolt, FaBan, FaEnvelope
} from 'react-icons/fa';
import { authAPI, postsAPI, usersAPI, bookmarksAPI, uploadAPI } from '../utils/api';
import { toast } from 'sonner';
import Sidebar from '../components/Sidebar';
import VerificationBadge from '../components/VerificationBadge';
import AvatarCropModal from '../components/AvatarCropModal';
import EditProfileModal from '../components/profile/EditProfileModal';
import ChallengeForm from '../components/profile/ChallengeForm';
import ProfileTabContent from '../components/profile/ProfileTabContent';
import SubscribeButton from '../components/SubscribeButton';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';

const PROFILE_TABS = [
  { id: 'posts', label: 'Points' },
  { id: 'media', label: 'Media' },
  { id: 'likes', label: 'Likes' },
];

const Profile = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [user, setUser] = useState(null);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'posts');
  const [followersCount, setFollowersCount] = useState(0);
  const [followingCount, setFollowingCount] = useState(0);
  const [isFollowing, setIsFollowing] = useState(false);
  const [isOwnProfile, setIsOwnProfile] = useState(false);
  const [bookmarks, setBookmarks] = useState([]);
  const [followers, setFollowers] = useState([]);
  const [following, setFollowing] = useState([]);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editData, setEditData] = useState({});
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [coverUploading, setCoverUploading] = useState(false);
  const [showChallengeModal, setShowChallengeModal] = useState(false);
  const [cropImageSrc, setCropImageSrc] = useState(null);
  const currentUser = JSON.parse(sessionStorage.getItem('user') || '{}');

  useEffect(() => { loadProfile(); }, [userId]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadProfile = async () => {
    setLoading(true);
    try {
      let profileUserId = userId;
      if (!profileUserId) {
        const meResponse = await authAPI.getMe();
        profileUserId = meResponse.data.id;
        setIsOwnProfile(true);
      } else {
        setIsOwnProfile(profileUserId === currentUser.id);
      }
      const profileResponse = await usersAPI.getProfile(profileUserId);
      setUser(profileResponse.data);
      setFollowersCount(profileResponse.data.followers_count);
      setFollowingCount(profileResponse.data.following_count);
      setIsFollowing(profileResponse.data.is_following);
      const postsResponse = await postsAPI.getUserPosts(profileUserId);
      setPosts(postsResponse.data);
      const [followersRes, followingRes] = await Promise.all([usersAPI.getFollowers(profileUserId), usersAPI.getFollowing(profileUserId)]);
      setFollowers(followersRes.data);
      setFollowing(followingRes.data);
      if (isOwnProfile || profileUserId === currentUser.id) {
        const bookmarksRes = await bookmarksAPI.getAll();
        setBookmarks(bookmarksRes.data);
      }
    } catch (error) {
      console.error('Failed to load profile', error);
      if (error.response?.status === 401) navigate('/');
    } finally { setLoading(false); }
  };

  const handleFollow = async () => {
    try {
      if (isFollowing) { await usersAPI.unfollow(user.id); } else { await usersAPI.follow(user.id); }
      setIsFollowing(!isFollowing);
      setFollowersCount(prev => isFollowing ? prev - 1 : prev + 1);
      toast.success(isFollowing ? 'Unfollowed' : 'Following!');
    } catch { toast.error('Failed to update follow status'); }
  };

  const handleUpdateProfile = async () => {
    try {
      await usersAPI.updateProfile(editData);
      toast.success('Profile updated!');
      setShowEditModal(false);
      const updatedUser = { ...currentUser };
      if (editData.full_name) updatedUser.full_name = editData.full_name;
      if (editData.username) updatedUser.username = editData.username;
      if (editData.avatar_url) updatedUser.avatar_url = editData.avatar_url;
      sessionStorage.setItem('user', JSON.stringify(updatedUser));
      loadProfile();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to update profile'); }
  };

  const handleAvatarUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Please select an image file'); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error('Image must be under 5MB'); return; }
    const reader = new FileReader();
    reader.onload = () => setCropImageSrc(reader.result);
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const handleCroppedAvatarUpload = async (croppedFile) => {
    setAvatarUploading(true);
    try {
      const res = await uploadAPI.uploadAvatar(croppedFile);
      const newUrl = res.data.url;
      setEditData(prev => ({ ...prev, avatar_url: newUrl }));
      setUser(prev => ({ ...prev, avatar_url: newUrl }));
      sessionStorage.setItem('user', JSON.stringify({ ...currentUser, avatar_url: newUrl }));
      setCropImageSrc(null);
      toast.success('Profile photo updated!');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to upload photo'); }
    finally { setAvatarUploading(false); }
  };

  const handleCoverUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setCoverUploading(true);
    try {
      const res = await uploadAPI.uploadMedia(file);
      setEditData(prev => ({ ...prev, cover_photo_url: res.data.url }));
      toast.success('Cover photo uploaded!');
    } catch { toast.error('Failed to upload cover photo'); }
    finally { setCoverUploading(false); }
  };

  const handlePinPost = async (postId) => {
    try { await usersAPI.pinPost(user.id, postId); toast.success('PinPoint set!'); loadProfile(); }
    catch { toast.error('Failed to set PinPoint'); }
  };

  const handleDeletePost = async (postId) => {
    if (!window.confirm('Delete this post?')) return;
    try { await postsAPI.delete(postId); setPosts(prev => prev.filter(p => p.id !== postId)); toast.success('Post deleted'); }
    catch { toast.error('Failed to delete post'); }
  };

  if (loading) {
    return <div className="min-h-screen bg-white flex items-center justify-center"><div className="w-8 h-8 border-2 border-[#4A90E2] border-t-transparent rounded-full animate-spin" /></div>;
  }
  if (!user) {
    return <div className="min-h-screen bg-white flex items-center justify-center"><p className="text-gray-500">User not found</p></div>;
  }

  const tabs = [
    ...PROFILE_TABS,
    { id: 'followers', label: `Followers (${followersCount})` },
    { id: 'following', label: `Following (${followingCount})` },
    ...(isOwnProfile ? [{ id: 'bookmarks', label: 'Bookmarks' }] : []),
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="grid grid-cols-1 md:grid-cols-12">
        <Sidebar />
        <main className="col-span-12 md:col-span-10">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            {/* Cover Photo */}
            <div className="h-48 md:h-64 bg-gradient-to-r from-[#4A90E2] to-[#A855F7] relative" style={user.cover_photo_url ? { backgroundImage: `url(${user.cover_photo_url})`, backgroundSize: 'cover', backgroundPosition: 'center' } : {}}>
              {isOwnProfile && (
                <button onClick={() => setShowEditModal(true)} className="absolute top-4 right-4 bg-white/20 backdrop-blur-sm text-white px-4 py-2 rounded-lg hover:bg-white/30 transition-colors flex items-center space-x-2"><FaCamera /><span>Edit Cover</span></button>
              )}
            </div>

            {/* Profile Header */}
            <div className="px-6 md:px-10 pb-6">
              <div className="flex flex-col md:flex-row md:items-end md:justify-between -mt-16 md:-mt-20">
                <div className="flex flex-col md:flex-row md:items-end md:space-x-6">
                  <div className="relative">
                    <img src={user.avatar_url || `https://ui-avatars.com/api/?name=${user.full_name}&size=150`} alt={user.full_name} className="w-32 h-32 md:w-40 md:h-40 rounded-full border-4 border-white object-cover shadow-lg" data-testid="profile-avatar" />
                    {isOwnProfile && (
                      <label className="absolute bottom-2 right-2 bg-[#4A90E2] text-white p-2 rounded-full hover:bg-[#3A80D2] transition-colors cursor-pointer" data-testid="profile-avatar-upload-btn">
                        {avatarUploading ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <FaCamera />}
                        <input type="file" accept="image/jpeg,image/png,image/gif,image/webp" onChange={handleAvatarUpload} className="hidden" data-testid="profile-avatar-file-input" />
                      </label>
                    )}
                  </div>
                  <div className="mt-4 md:mt-0 md:pb-4">
                    <div className="flex items-center space-x-2">
                      <h1 className="text-2xl md:text-3xl font-outfit font-bold text-gray-900">{user.full_name}</h1>
                      {user.is_verified && <VerificationBadge badgeType={user.verification_badge} size="lg" />}
                    </div>
                    <p className="text-gray-500 text-lg">@{user.username}</p>
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-2 mt-4 md:mt-0">
                  {isOwnProfile ? (
                    <>
                      <button onClick={() => setShowEditModal(true)} className="flex items-center space-x-1 px-2.5 py-1 bg-white border border-gray-200 text-gray-700 rounded-md hover:bg-gray-50 transition-colors text-xs"><FaEdit size={10} /><span>Edit Profile</span></button>
                      <button onClick={() => navigate('/verification')} className="flex items-center space-x-1 px-2.5 py-1 bg-gradient-to-r from-[#4A90E2] to-[#A855F7] text-white rounded-md hover:shadow-lg transition-all text-xs"><FaCheckCircle size={10} /><span>Get Verified</span></button>
                    </>
                  ) : (
                    <>
                      <button onClick={handleFollow} className={`flex items-center space-x-1.5 px-3 py-1.5 md:px-5 md:py-2.5 rounded-lg font-medium text-sm md:text-base transition-all ${isFollowing ? 'bg-white border border-gray-200 text-gray-700 hover:bg-red-50 hover:text-red-500 hover:border-red-200' : 'bg-gradient-to-r from-[#4A90E2] to-[#A855F7] text-white hover:shadow-lg'}`}>
                        {isFollowing ? <FaUserMinus size={12} /> : <FaUserPlus size={12} />}<span>{isFollowing ? 'Unfollow' : 'Follow'}</span>
                      </button>
                      <button onClick={() => navigate(`/messages?startChat=${user.id}`)} data-testid="message-user-btn" className="flex items-center space-x-1.5 px-3 py-1.5 md:px-5 md:py-2.5 rounded-lg font-medium text-sm md:text-base bg-white border border-gray-200 text-gray-700 hover:bg-[#4A90E2] hover:text-white hover:border-[#4A90E2] transition-all"><FaEnvelope size={12} /><span>Message</span></button>
                      <button onClick={() => setShowChallengeModal(true)} data-testid="challenge-user-btn" className="flex items-center space-x-1.5 px-3 py-1.5 md:px-5 md:py-2.5 rounded-lg font-medium text-sm md:text-base bg-gray-900 text-white hover:bg-gray-800 transition-all"><FaBolt size={12} /><span>Challenge</span></button>
                      <SubscribeButton userId={user.id} currentUserId={currentUser.id} />
                      <button onClick={async () => { if (!window.confirm('Block this user?')) return; try { const { securityAPI } = await import('../utils/api'); await securityAPI.blockUser(user.id); toast.success('User blocked'); navigate('/dashboard'); } catch (e) { toast.error(e.response?.data?.detail || 'Failed to block'); } }} data-testid="block-user-btn" className="flex items-center space-x-1.5 px-3 py-1.5 md:px-4 md:py-2.5 rounded-lg font-medium text-sm bg-red-50 text-red-500 hover:bg-red-100 transition-all"><FaBan size={12} /></button>
                    </>
                  )}
                </div>
              </div>
              <div className="mt-6 max-w-2xl">
                {user.bio && <p className="text-gray-700 text-lg mb-4">{user.bio}</p>}
                <div className="flex flex-wrap items-center gap-4 text-gray-500">
                  {user.location && <span className="flex items-center space-x-1"><FaMapMarkerAlt /><span>{user.location}</span></span>}
                  {user.website_url && <a href={user.website_url} target="_blank" rel="noopener noreferrer" className="flex items-center space-x-1 text-[#4A90E2] hover:underline"><FaLink /><span>{user.website_url.replace(/https?:\/\//, '')}</span></a>}
                  <span className="flex items-center space-x-1"><FaCalendarAlt /><span>Joined {new Date(user.created_at).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}</span></span>
                </div>
                <div className="flex items-center space-x-6 mt-4">
                  <button onClick={() => setActiveTab('followers')} className="hover:underline"><span className="font-bold text-gray-900">{followersCount}</span><span className="text-gray-500 ml-1">Followers</span></button>
                  <button onClick={() => setActiveTab('following')} className="hover:underline"><span className="font-bold text-gray-900">{followingCount}</span><span className="text-gray-500 ml-1">Following</span></button>
                  <span><span className="font-bold text-gray-900">{posts.length}</span><span className="text-gray-500 ml-1 font-arthaus">Points</span></span>
                </div>
              </div>
            </div>

            {/* Tabs */}
            <div className="border-b border-gray-200 px-6 md:px-10">
              <div className="flex space-x-8 overflow-x-auto">
                {tabs.map(tab => (
                  <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`py-4 border-b-2 font-medium transition-colors whitespace-nowrap ${activeTab === tab.id ? 'border-[#4A90E2] text-[#4A90E2]' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
                    <span className={tab.label.includes('Point') ? 'font-arthaus' : ''}>{tab.label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Content */}
            <div className="p-6 md:p-10">
              {user.pinned_post && activeTab === 'posts' && (
                <div className="mb-6">
                  <div className="flex items-center space-x-2 text-gray-500 mb-3"><FaThumbtack className="text-[#4A90E2]" /><span className="text-sm font-medium font-arthaus">PinPoint</span></div>
                  <div className="bg-white border-2 border-[#4A90E2]/20 rounded-xl p-6"><p className="text-gray-700">{user.pinned_post.content}</p></div>
                </div>
              )}
              <ProfileTabContent activeTab={activeTab} posts={posts} bookmarks={bookmarks} followers={followers} following={following} isOwnProfile={isOwnProfile} onPinPost={handlePinPost} onDeletePost={handleDeletePost} />
            </div>
          </motion.div>
        </main>
      </div>

      <EditProfileModal show={showEditModal} user={user} editData={editData} setEditData={setEditData} onClose={() => setShowEditModal(false)} onSave={handleUpdateProfile} avatarUploading={avatarUploading} coverUploading={coverUploading} onAvatarUpload={handleAvatarUpload} onCoverUpload={handleCoverUpload} />

      <AnimatePresence>
        {showChallengeModal && user && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4" onClick={() => setShowChallengeModal(false)}>
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }} onClick={e => e.stopPropagation()} className="bg-white rounded-2xl shadow-xl w-full max-w-md">
              <ChallengeForm opponent={user} onClose={() => setShowChallengeModal(false)} />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {cropImageSrc && <AvatarCropModal imageSrc={cropImageSrc} onClose={() => setCropImageSrc(null)} onCropDone={handleCroppedAvatarUpload} />}
      </AnimatePresence>
    </div>
  );
};

export default Profile;
